import { FormSubmissionSection } from "#batteries-included-components/Forms/FormSubmissionForm/FormSubmissionSections";
import { FormSubmissionField } from "#batteries-included-components/Forms/FormSubmissionForm/FormSubmissionSections/FormSubmissionField";
import { DEFAULT_QUERY_OPTIONS } from "#components/hooks/adapters/adapterUtils";
import { useBreadcrumbs } from "#src/Routers/breadcrumbsHelper";
import { useNavigate, useParams, useSearchParams } from "#src/Routers/hooks";
import { linkToFormSubmissionDetail } from "#src/Routers/links";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useGetOneWorkflowTask } from "#src/components/hooks/adapters/useWorkflowTasks";
import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import { FORMS_BREADCRUMB } from "#src/routes/forms";
import { FORMS_CATEGORIES_BREADCRUMB } from "#src/routes/forms/categories";
import { WorkflowDetailsRoutePath } from "#src/routes/workflows/all/[workflowId]/detail";
import { ExceptionUtils } from "#src/utils/exception";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Banner,
  Button,
  Form,
  generateDocumentTitle,
  Link,
  Page,
  useForm,
  useToast,
} from "@validereinc/common-components";
import {
  EventAdapter,
  FormCategoryAdapter,
  FormSchemaAdapter,
  FormSubmissionAdapter,
  FormSubmissionStatus,
  FormSubmissionType,
  ResourceDefinitions,
  Resources,
} from "@validereinc/domain";
import { Forms } from "@validereinc/domain-controllers";
import { BaseError } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { Base64 } from "js-base64";
import React, { useEffect, useMemo } from "react";
import { Titled } from "react-titled";
import { CREATE_FORM_SUBMISSION_BREADCRUMB } from ".";
import { FORM_TEMPLATE_DETAILS_BREADCRUMB, linkToFormTemplateDetail } from "..";
import { FORM_CATEGORY_DETAILS_BREADCRUMB } from "../../..";
import styles from "./CreateFormSubmissionPage.module.scss";

const {
  getAnswersForSubmission,
  getErrorCount,
  getSmartDefaultValues,
  useFieldNamesToDisable,
  useGenerateFieldsToWatch,
  useGenerateLookupQueries,
  useProcessAttributeLookupQueries,
  useProcessCalculatedFields,
  useProcessConditionalOverrides,
  useResolvedFormSchema,
} = Forms.logic;
const { FormSubmissionFormController } = Forms.view;
const cx = classNames.bind(styles);

export const CreateFormSubmissionPage = () => {
  const navigate = useNavigate();

  const { categoryId, formTemplateId } = useParams<{
    categoryId: string;
    formTemplateId: string;
  }>();

  const { toast } = useToast();

  const {
    v2: {
      userInfo: { user },
    },
  } = useAuthenticatedContext();

  const [
    {
      "event-id": eventId,
      "task-id": workflowTaskId,
      "associated-asset-id": associatedAssetId,
      "associated-asset-type": associatedAssetType,
      "default-values": serializedEncodedDefaultValues,
    },
  ] = useSearchParams();

  const initialValues = useMemo(() => {
    let value = {} as Record<string, string>;
    if (!serializedEncodedDefaultValues) return value;
    try {
      const decodedDefaultValues = Base64.decode(
        serializedEncodedDefaultValues
      );
      value = JSON.parse(decodedDefaultValues) as Record<string, string>;
    } catch (err) {
      ExceptionUtils.reportException(err, "error", {
        hint: `Default values (URLParam: default-values) couldn't be decoded and parsed: ${serializedEncodedDefaultValues}`,
      });
    }
    return value;
  }, [serializedEncodedDefaultValues]);

  const query = useQuery({
    queryKey: [Resources.FORM_SCHEMA, formTemplateId] as const,
    queryFn: ({ queryKey: [_, formSchemaId] }) =>
      FormSchemaAdapter.getOne({ id: formSchemaId }),
    ...DEFAULT_QUERY_OPTIONS,
    select: (resp) => resp?.data,
  });

  const taskQuery = useGetOneWorkflowTask(
    { id: workflowTaskId },
    { enabled: !!workflowTaskId }
  );
  const task = taskQuery.data?.data;

  const workflowName =
    task?.workflow.name ?? task?.workflow.workflow_template.name;
  const formSchema = query.data;

  const now = useMemo(() => new Date().toISOString(), []);

  const calculatedDefaultAnswers = getSmartDefaultValues(formSchema, {
    now,
    currentUserName: user?.name,
    associatedAssetId,
    associatedAssetType,
    defaultValues: initialValues,
  });

  const form = useForm<Pick<FormSubmissionType, "answers">>({
    defaultValues: calculatedDefaultAnswers,
  });
  const formValues = form.watch();

  // Resolve the form schema with active conditional overrides applied
  const resolvedFormSchema = useResolvedFormSchema({
    formSchema,
    formValues,
  });

  const { fieldsToWatch, evaluationOrder } = useGenerateFieldsToWatch({
    formSchema: resolvedFormSchema,
    formValues,
  });

  /**
   * When receiving UUID default values for a form submission (from associatedAssetId), its related custom attributes should be fetched
   * and not wait for the user to interact with the form.
   */
  const serializedFormValues = JSON.stringify(formValues);
  const prepopulatedLookupFieldsToQuery = useMemo(() => {
    const formValues = JSON.parse(serializedFormValues);

    const output: string[] = [];
    if (!formSchema) return output;

    const questionIds: string[] = [];
    if (associatedAssetId && associatedAssetType) {
      questionIds.push(
        ...Object.entries(formSchema?.config?.questions ?? {})
          .filter(
            ([_, question]) =>
              "lookup_entity_type" in question &&
              question.lookup_entity_type &&
              question.lookup_entity_type === associatedAssetType
          )
          .map((entry) => entry[0])
      );
    }

    if (initialValues) {
      Object.keys(formSchema.config.questions).forEach((qid) => {
        if (
          initialValues[`$.questions.${qid}.default_answer`] !== undefined &&
          initialValues[`$.questions.${qid}.default_answer`] !== null
        ) {
          const question = formSchema.config.questions[qid];
          if ("lookup_entity_type" in question && question.lookup_entity_type) {
            questionIds.push(qid);
          }
        }
      });
    }

    (formSchema?.config?.sections ?? []).forEach((section) => {
      if (!formValues?.answers?.[section.id]?.length) return;
      for (
        let sectionIndex = 0;
        sectionIndex < formValues.answers[section.id].length;
        sectionIndex++
      ) {
        questionIds.forEach((questionId) => {
          if (section.questions.includes(questionId))
            output.push(`answers.${section.id}.${sectionIndex}.${questionId}`);
        });
      }
    });

    return output;
  }, [
    associatedAssetId,
    associatedAssetType,
    formSchema,
    serializedFormValues,
    calculatedDefaultAnswers,
  ]);
  const attributeLookupQueryDetails = useGenerateLookupQueries({
    watchedFields: fieldsToWatch,
    formValues,
    dirtyFields: form.formState.dirtyFields,
    questionPathsToForcePopulate: prepopulatedLookupFieldsToQuery,
  });

  const attributeLookupQueries = useQueries({
    queries: attributeLookupQueryDetails.map((q) => q.queryProps),
  });

  const { fieldsNamesToDisable: lookupFieldNamesToDisable } =
    useProcessAttributeLookupQueries({
      queries: attributeLookupQueries,
      queriesDetails: attributeLookupQueryDetails,
      watchedFields: fieldsToWatch,
      form,
      formSchema: resolvedFormSchema,
      dirtyFields: form.formState.dirtyFields,
    });

  const { fieldsNamesToDisable: defaultAnswersFieldNamesToDisable } =
    useFieldNamesToDisable(
      resolvedFormSchema,
      formValues,
      initialValues,
      undefined
    );

  const fieldsNamesToDisable = useMemo(() => {
    return new Set([
      ...lookupFieldNamesToDisable,
      ...defaultAnswersFieldNamesToDisable,
    ]);
  }, [lookupFieldNamesToDisable, defaultAnswersFieldNamesToDisable]);

  const {
    watchedFieldsMap: watchedFieldsState,
    pendingFormUpdates: calculatedFieldUpdates,
  } = useProcessCalculatedFields({
    evaluationOrder,
    watchedFields: fieldsToWatch,
    formValues,
    formSchema: resolvedFormSchema,
  });

  const { pendingFormUpdates: conditionalOverrideUpdates } =
    useProcessConditionalOverrides({
      formValues,
      formSchema: resolvedFormSchema,
      dirtyFields: form.formState.dirtyFields,
    });

  const pendingFormUpdates = useMemo(
    () =>
      [
        ...calculatedFieldUpdates,
        ...conditionalOverrideUpdates,
      ] as typeof calculatedFieldUpdates,
    [calculatedFieldUpdates, conditionalOverrideUpdates]
  );

  const queryClient = useQueryClient();

  const categoryQuery = useQuery({
    queryKey: [Resources.FORM_CATEGORY, categoryId],
    queryFn: async ({ queryKey }) => {
      const [_, id] = queryKey;

      return await FormCategoryAdapter.getOne({
        id,
      });
    },
  });

  const templateQuery = useQuery({
    queryKey: [Resources.FORM_SCHEMA, formTemplateId],
    queryFn: async ({ queryKey }) => {
      const [_, formSchemaId] = queryKey;

      return await FormSchemaAdapter.getOne({
        id: formSchemaId,
      });
    },
    staleTime: 2 * 60 * 1000,
    select: (resp) => resp?.data,
  });

  const breadcrumbs = useBreadcrumbs(
    [
      FORMS_BREADCRUMB,
      FORMS_CATEGORIES_BREADCRUMB,
      FORM_CATEGORY_DETAILS_BREADCRUMB,
      FORM_TEMPLATE_DETAILS_BREADCRUMB,
      CREATE_FORM_SUBMISSION_BREADCRUMB,
    ],
    {
      2: categoryQuery?.data?.name,
      3: templateQuery?.data?.name,
    }
  );

  const mutation = useMutation({
    mutationFn: async (
      formValues: Parameters<typeof FormSubmissionAdapter.createOne>[0]["data"]
    ) => {
      if (!formSchema) throw new BaseError("Form schema not available");

      const body = {
        data: {
          ...formValues,
          answers: getAnswersForSubmission(formValues?.answers, formSchema),
          workflow_task_id: workflowTaskId,
        },
      };

      const {
        data: { id, ...restResponse },
      } = await FormSubmissionAdapter.createOne(body);

      if (eventId) {
        await EventAdapter.formSubmissions.createOne({
          formSubmissionId: id,
          eventId,
        });

        queryClient.invalidateQueries({
          queryKey: ["event", { id: eventId }],
        });
      }

      return { id, ...restResponse };
    },
    onSuccess: ({ id }, formValues) => {
      queryClient.invalidateQueries({ queryKey: [Resources.WORKFLOW] });
      queryClient.invalidateQueries({
        queryKey: [Resources.WORKFLOW, "tasks"],
      });
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({
        queryKey: ["forms", "submissions"],
      });
      queryClient.invalidateQueries({
        queryKey: [Resources.FORM_SUBMISSION],
      });
      setTimeout(() => {
        queryClient.refetchQueries({
          queryKey: [Resources.WORKFLOW, "tasks"],
        });
        queryClient.refetchQueries({
          queryKey: [Resources.WORKFLOW],
        });
      }, 4000);

      toast.push({
        intent: "success",
        description: (
          <>
            {formValues.status !== "draft" ? (
              <>
                Successfully created form submission.{" "}
                <Link
                  label="View Details"
                  underline="always"
                  onClick={() =>
                    navigate({
                      pathname: linkToFormSubmissionDetail(id),
                    })
                  }
                />
              </>
            ) : (
              "Successfully saved form as draft."
            )}
          </>
        ),
      });

      form.reset({}, { keepDirty: false });

      if (workflowTaskId || eventId) {
        navigate.goBack();
      } else {
        navigate({
          pathname: linkToFormTemplateDetail(categoryId, formTemplateId),
          query: {
            "submission-type": formValues.status === "draft" ? "draft" : "all",
          },
          replace: true,
        });
      }
    },
    onError: (err, formValues) => {
      toast.push({
        intent: "error",
        description:
          formValues.status !== "draft"
            ? "Failed to create form submission"
            : "Failed to save form as draft.",
      });
      console.error(err);
    },
  });

  const onError = (errors: Record<string, unknown>) => {
    console.error("Error submitting form", errors);
  };

  const onCancel = () => {
    navigate.goBack();
  };

  const onSubmit = form.handleSubmit((formValues) => {
    if (!formSchema) throw new BaseError("Form schema not available");

    mutation.mutate({
      answers: getAnswersForSubmission(formValues?.answers, formSchema),
      form_schema_id: formTemplateId,
    });
  }, onError);

  const onSaveAsDraft = () => {
    if (!formSchema) throw new BaseError("Form schema not available");

    const formValues = form.getValues();

    mutation.mutate({
      answers: getAnswersForSubmission(formValues?.answers, formSchema),
      form_schema_id: formTemplateId,
      status: FormSubmissionStatus.DRAFT,
    });
  };

  useEffect(() => {
    pendingFormUpdates?.forEach(({ path, value, options }) => {
      form.setValue(path, value, options);
    });
  }, [JSON.stringify(pendingFormUpdates)]);

  const errorCount = getErrorCount(form);

  return (
    <Titled
      title={generateDocumentTitle(
        templateQuery.data?.name ??
          ResourceDefinitions.form_schema.label.singular,
        breadcrumbs?.at(-1)?.title ?? ""
      )}
    >
      <Page
        isLoading={query?.isLoading}
        category={CREATE_FORM_SUBMISSION_BREADCRUMB.title}
        title={templateQuery.data?.name}
        breadcrumbs={breadcrumbs}
        footer={
          <div className={cx("footerContainer")}>
            <Button
              key="cancel-action"
              onClick={onCancel}
            >
              Cancel
            </Button>

            <div className={cx("footerActionsContainer")}>
              {errorCount ? (
                <p className={cx("errorCount")}>
                  {errorCount
                    ? `${errorCount} field${
                        errorCount > 1 ? "s have" : " has"
                      } an error. Please fix the error${
                        errorCount > 1 ? "s" : ""
                      } to submit the form.`
                    : ""}
                </p>
              ) : null}

              <Button
                key="save-as-draft-action"
                onClick={onSaveAsDraft}
                isLoading={
                  mutation.isLoading && mutation.variables?.status === "draft"
                }
                disabled={mutation.isLoading || !!errorCount}
              >
                Save as Draft
              </Button>

              <Button
                key="submit-action"
                variant="primary"
                onClick={onSubmit}
                isLoading={
                  mutation.isLoading && mutation.variables?.status !== "draft"
                }
                disabled={mutation.isLoading || !!errorCount}
              >
                Submit
              </Button>
            </div>
          </div>
        }
      >
        {task && !taskQuery.isLoading ? (
          <Banner
            className={cx("banner")}
            titleText={`This submission will be associated to a workflow task "${task?.name}" in the Workflow "${workflowName}"`}
            actionContent={
              <RoutingLink
                to={WorkflowDetailsRoutePath.toLinkParts({
                  pathParams: {
                    workflowId: task?.workflow_id,
                  },
                })}
              >
                View Workflow
              </RoutingLink>
            }
            isDismissable
          />
        ) : null}

        <Form {...form}>
          <FormSubmissionFormController
            form={form}
            formSchema={resolvedFormSchema}
            sectionRenderFunction={(all) => <FormSubmissionSection {...all} />}
            questionRenderFunction={(all) => {
              return (
                <FormSubmissionField
                  {...all}
                  key={all.name}
                  isDisabled={fieldsNamesToDisable.has(all.name)}
                  isReady={watchedFieldsState[all.dataKey]?.areSourcesReady}
                  isBusy={watchedFieldsState[all.dataKey]?.isCalculating}
                  isValid={watchedFieldsState[all.dataKey]?.isCalculatable}
                  {...(watchedFieldsState[all.dataKey]?.value.calculation ??
                    {})}
                  questionsMap={resolvedFormSchema!.config.questions}
                />
              );
            }}
            // This prop is needed when adding a repeatable section:
            defaultAnswers={calculatedDefaultAnswers}
          />
        </Form>
      </Page>
    </Titled>
  );
};
